// Generated types based on the Klubbspel API schema
// This would normally be auto-generated from the OpenAPI spec

export interface ApiError {
  code: string
  message: string
  details?: unknown[]
}

export interface ApiResponse<T> {
  data?: T
  error?: ApiError
}

// Common types
export interface Id {
  id: string
}

export interface TimeRange {
  start: string // ISO timestamp
  end: string   // ISO timestamp
}

export type Sport =
  | 'SPORT_UNSPECIFIED'
  | 'SPORT_TABLE_TENNIS'
  | 'SPORT_TENNIS'
  | 'SPORT_PADEL'
  | 'SPORT_BADMINTON'
  | 'SPORT_SQUASH'
  | 'SPORT_PICKLEBALL'
  | 'SPORT_RACQUETBALL'
  | 'SPORT_BEACH_TENNIS'
  | 'SPORT_CHESS'
  | 'SPORT_TABLE_HOCKEY'
  | 'SPORT_FOOSBALL'

// Map Sport enum names to their numeric values (must match proto enum)
export const SportEnumValue: Record<Sport, number> = {
  SPORT_UNSPECIFIED: 0,
  SPORT_TABLE_TENNIS: 1,
  SPORT_TENNIS: 2,
  SPORT_PADEL: 3,
  SPORT_BADMINTON: 4,
  SPORT_SQUASH: 5,
  SPORT_PICKLEBALL: 6,
  SPORT_RACQUETBALL: 7,
  SPORT_BEACH_TENNIS: 8,
  SPORT_CHESS: 9,
  SPORT_TABLE_HOCKEY: 10,
  SPORT_FOOSBALL: 11,
}

export type ScoringProfile =
  | 'SCORING_PROFILE_UNSPECIFIED'
  | 'SCORING_PROFILE_TABLE_TENNIS_SETS'
  | 'SCORING_PROFILE_SCORELINE'
  | 'SCORING_PROFILE_STROKE_CARD'
  | 'SCORING_PROFILE_WEIGH_IN'
  | 'SCORING_PROFILE_MATCH_OUTCOME'

export type SeriesFormat =
  | 'SERIES_FORMAT_UNSPECIFIED'
  | 'SERIES_FORMAT_OPEN_PLAY'
  | 'SERIES_FORMAT_LADDER'
  | 'SERIES_FORMAT_CUP'
  | 'SERIES_FORMAT_GROUP_KNOCKOUT'

export type LadderRules =
  | 'LADDER_RULES_UNSPECIFIED'
  | 'LADDER_RULES_CLASSIC'
  | 'LADDER_RULES_AGGRESSIVE'

export type KnockoutBracketState =
  | 'KNOCKOUT_BRACKET_STATE_UNSPECIFIED'
  | 'KNOCKOUT_BRACKET_STATE_DRAFT'
  | 'KNOCKOUT_BRACKET_STATE_ACTIVE'
  | 'KNOCKOUT_BRACKET_STATE_COMPLETED'

export type KnockoutMatchOutcome =
  | 'KNOCKOUT_MATCH_OUTCOME_UNSPECIFIED'
  | 'KNOCKOUT_MATCH_OUTCOME_PLAYER_A_WIN'
  | 'KNOCKOUT_MATCH_OUTCOME_PLAYER_B_WIN'

export type KnockoutSeedingStrategy =
  | 'KNOCKOUT_SEEDING_STRATEGY_UNSPECIFIED'
  | 'KNOCKOUT_SEEDING_STRATEGY_MANUAL'
  | 'KNOCKOUT_SEEDING_STRATEGY_RANDOM'
  | 'KNOCKOUT_SEEDING_STRATEGY_WIN_RATE'
  | 'KNOCKOUT_SEEDING_STRATEGY_HYBRID'

// Club types
export interface Club {
  id: string
  name: string
  supportedSports: Sport[]
  seriesSports?: Sport[]
}

export interface CreateClubRequest {
  name: string
  supportedSports?: Sport[]
}

export interface CreateClubResponse {
  club: Club
}

export interface UpdateClubRequest {
  name?: string
  supportedSports?: Sport[]
}

export interface ListClubsRequest {
  searchQuery?: string
  pageSize?: number
  cursorAfter?: string
  cursorBefore?: string
}

export interface ListClubsResponse {
  items: Club[]
  startCursor?: string
  endCursor?: string
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// Player types
export interface PlayerSportStats {
  sport: Sport
  totalMatchesPlayed: number
  totalMatchesWon: number
  totalMatchesLost: number
  totalMatchesDrawn: number
  winRate: number
  statsLastUpdated?: string
}

export interface Player {
  id: string
  displayName: string
  active: boolean
  email: string
  firstName: string
  lastName: string
  clubMemberships: ClubMembership[]
  isPlatformOwner: boolean
  createdAt: string
  lastLoginAt?: string
  statsBySport: Record<string, PlayerSportStats>
}

// Club membership for players
export interface ClubMembership {
  clubId: string
  role: 'MEMBERSHIP_ROLE_MEMBER' | 'MEMBERSHIP_ROLE_ADMIN' | 'MEMBERSHIP_ROLE_UNSPECIFIED' // i18n-ignore: API constants
  active: boolean
  joinedAt: string
  leftAt?: string
}

export interface CreatePlayerRequest {
  displayName: string  // Frontend uses camelCase for requests
  initialClubId: string
}

export interface CreatePlayerResponse {
  player: Player
  similar: Player[]
}

export interface UpdatePlayerRequest {
  displayName?: string
  firstName?: string
  lastName?: string
  clubId?: string
  active?: boolean
}

export interface ListPlayersRequest {
  searchQuery?: string
  clubId?: string // DEPRECATED: Use clubFilter instead
  clubFilter?: string[] // Support multiple club IDs and special "OPEN" value
  excludedIds?: string[] // Player IDs to exclude from results
  pageSize?: number
  cursorAfter?: string
  cursorBefore?: string
}

export interface ListPlayersResponse {
  items: Player[]
  startCursor?: string
  endCursor?: string
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface MergePlayerRequest {
  sourcePlayerId: string  // The player to merge from (usually non-authenticated)
}

export interface MergePlayerResponse {
  player: Player         // The resulting merged player
  matchesUpdated: number // Number of matches that were updated
  tokensUpdated: number  // Number of tokens that were updated
}

export interface FindMergeCandidatesRequest {
  clubId?: string      // Optional club ID to limit search to specific club
  namePattern?: string // Optional name pattern to search for
}

export interface MergeCandidate {
  player: Player        // The email-less player that could be merged
  similarityScore: number // Similarity score between candidate and authenticated user (0.0 to 1.0)
}

export interface FindMergeCandidatesResponse {
  candidates: MergeCandidate[] // List of email-less players with similarity scores
}

// Series types
export type SeriesVisibility = 'SERIES_VISIBILITY_OPEN' | 'SERIES_VISIBILITY_CLUB_ONLY' | 'SERIES_VISIBILITY_UNSPECIFIED'

export type SeriesState =
  | 'SERIES_STATE_UNSPECIFIED'
  | 'SERIES_STATE_ACTIVE'
  | 'SERIES_STATE_COMPLETED'

// Rules types
export interface RuleExample {
  scenario: string
  outcome: string
}

export interface RulesDescription {
  title: string
  summary: string
  rules: string[]
  examples: RuleExample[]
}

export interface GetSeriesRulesRequest {
  format: SeriesFormat
  ladderRules?: LadderRules
  sport?: Sport
}

export interface GetSeriesRulesResponse {
  rules: RulesDescription
}

export interface CompleteSeriesResponse {
  series: Series
  podium: Podium
}

export interface Series {
  id: string
  clubId?: string
  title: string
  startsAt: string  // Backend sends startsAt per swagger
  endsAt: string    // Backend sends endsAt per swagger
  visibility: SeriesVisibility
  sport: Sport
  format: SeriesFormat
  ladderRules?: LadderRules
  scoringProfile: ScoringProfile
  setsToPlay: number  // For table tennis: 3 or 5
  cupBronzeMedalMatch?: boolean
  hasGroupStage?: boolean
  state?: SeriesState
  podium?: Podium
}

export interface Podium {
  placements: PodiumPlacement[]
  completedAt: string
}

export interface PodiumPlacement {
  placement: number  // 1, 2, or 3
  playerIds: string[]
  playerName?: string
  stats: PodiumStats
}

export interface PodiumStats {
  wins: number
  losses: number
  setDifference: number
  eloRating?: number
  ladderPosition?: number
}

export interface CreateSeriesRequest {
  clubId?: string
  title: string
  startsAt: string
  endsAt: string
  visibility: SeriesVisibility
  sport?: Sport
  format?: SeriesFormat
  ladderRules?: LadderRules
  scoringProfile?: ScoringProfile
  setsToPlay?: number
  cupBronzeMedalMatch?: boolean
  hasGroupStage?: boolean
}

export interface UpdateSeriesRequest {
  title?: string
  startsAt?: string
  endsAt?: string
  visibility?: SeriesVisibility
  sport?: Sport
  format?: SeriesFormat
  hasGroupStage?: boolean
}

export interface ListSeriesRequest {
  pageSize?: number
  cursorAfter?: string
  cursorBefore?: string
  sportFilter?: Sport
  clubFilter?: string[]  // Club IDs to filter by, special value "OPEN" for open series
}

export interface ListSeriesResponse {
  items: Series[]
  startCursor?: string
  endCursor?: string
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// Match types

export type GroupStageState =
  | 'GROUP_STAGE_STATE_UNSPECIFIED'
  | 'GROUP_STAGE_STATE_PENDING'
  | 'GROUP_STAGE_STATE_ACTIVE'
  | 'GROUP_STAGE_STATE_COMPLETED'

export interface GroupStageMatch {
  id: string
  groupId: string
  playerAId: string
  playerBId: string
  scoreA?: number
  scoreB?: number
  winnerId?: string
  matchTime?: string
  completed: boolean
  isLocked?: boolean
  lockedReason?: string
}

export interface GroupStanding {
  playerId: string
  matchesPlayed: number
  wins: number
  losses: number
  draws: number
  points: number
  rank: number
  scoreFor: number
  scoreAgainst: number
}

export interface Group {
  id: string
  name: string
  playerIds: string[]
  matches: GroupStageMatch[]
  standings: GroupStanding[]
}

export interface GroupStage {
  state: GroupStageState
  groups: Group[]
  playersPerGroup: number
  advancePerGroup: number
  createdAt: string
  completedAt?: string
}
export interface MatchView {
  id: string
  seriesId: string
  playerAName: string
  playerBName: string
  scoreA: number
  scoreB: number
  playedAt: string
  outcome?: MatchOutcomeResultOutcome
  playerAId: string
  playerBId: string
}

export interface ReportMatchRequest {
  seriesId: string
  playedAt: string
  participantA?: MatchParticipant
  participantB?: MatchParticipant
  matchResult?: MatchResult
  // Legacy fields maintained for backward compatibility while backend transitions
  playerAId?: string
  playerBId?: string
  scoreA?: number
  scoreB?: number
}

export interface ReportMatchResponse {
  matchId: string
}

export interface MatchParticipant {
  playerId?: string
  teamId?: string
}

export interface TableTennisResult {
  setsA: number
  setsB: number
}

export interface ScorelineResult {
  scoreA: number
  scoreB: number
  shootout?: boolean
}

export interface StrokeCardResult {
  totalStrokes: number
}

export interface WeighInResult {
  totalWeightKg: number
  count: number
}

export interface MatchResult {
  tableTennis?: TableTennisResult
  scoreline?: ScorelineResult
  strokeCard?: StrokeCardResult
  weighIn?: WeighInResult
  matchOutcome?: MatchOutcomeResult
}

export type MatchOutcomeResultOutcome =
  | 'OUTCOME_UNSPECIFIED'
  | 'OUTCOME_PLAYER_A_WIN'
  | 'OUTCOME_PLAYER_B_WIN'
  | 'OUTCOME_DRAW'

export interface MatchOutcomeResult {
  outcome: MatchOutcomeResultOutcome
}

export interface ListMatchesRequest {
  seriesId: string
  pageSize?: number
  cursorAfter?: string
  cursorBefore?: string
}

export interface ListMatchesResponse {
  items: MatchView[]
  startCursor?: string
  endCursor?: string
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface UpdateMatchRequest {
  matchId: string
  scoreA?: number
  scoreB?: number
  playedAt?: string
}

export interface UpdateMatchResponse {
  match: MatchView
}

export interface DeleteMatchRequest {
  matchId: string
}

export interface DeleteMatchResponse {
  // Empty response
}

// Leaderboard types
export interface LeaderboardEntry {
  rank: number
  playerId: string
  playerName: string
  eloRating: number
  matchesPlayed: number
  matchesWon: number
  matchesDrawn: number
  matchesLost: number
  winRate: number
  gamesWon: number
  gamesLost: number
  gameWinRate: number
  rankChange: number
}

export interface GetLeaderboardRequest {
  seriesId: string
  pageSize?: number
  cursorAfter?: string
  cursorBefore?: string
}

export interface GetLeaderboardResponse {
  entries: LeaderboardEntry[]
  startCursor?: string
  endCursor?: string
  hasNextPage: boolean
  hasPreviousPage: boolean
  totalPlayers: number
  lastUpdated: string
}

// Knockout tournament types
export interface KnockoutMatch {
  id: string
  roundNumber: number
  positionInRound: number
  playerAId?: string
  playerBId?: string
  winnerId?: string
  sourceMatchA?: string
  sourceMatchB?: string
  scoreA?: number
  scoreB?: number
  outcome: KnockoutMatchOutcome
  matchTime?: string
  completed: boolean
  isLocked?: boolean
  lockedReason?: string
  isBronzeMatch?: boolean
}

export interface KnockoutBracket {
  id: string
  seriesId: string
  bracketSize: number
  totalRounds: number
  state: KnockoutBracketState
  matches: KnockoutMatch[]
  hasBronzeMedalMatch?: boolean
  bronzeMedalMatchId?: string
  hasGroupStage?: boolean
  groupStage?: GroupStage
  createdAt?: string
  updatedAt?: string
  completedAt?: string
}

export interface CreateKnockoutBracketRequest {
  playerIds: string[]
  bracketSize: number
  seedingStrategy?: KnockoutSeedingStrategy
  hybridSeedCount?: number
  bronzeMedalMatch?: boolean
}

export interface CreateKnockoutBracketResponse {
  bracket: KnockoutBracket
}

export interface GetKnockoutBracketResponse {
  bracket?: KnockoutBracket
}

export interface ReportKnockoutMatchRequest {
  bracketId: string
  matchId: string
  scoreA: number
  scoreB: number
  matchTime?: string
}

export interface ReportKnockoutMatchResponse {
  match: KnockoutMatch
  bracket: KnockoutBracket
}

export interface ListKnockoutMatchesResponse {
  matches: KnockoutMatch[]
}

export interface CheckMatchEditabilityResponse {
  isEditable: boolean
  reason?: string
}

export interface CreateGroupStageRequest {
  seriesId: string
  playerIds: string[]
  groupCount: number
  playersPerGroup: number
  advancePerGroup: number
  bronzeMedalMatch?: boolean
}

export interface CreateGroupStageResponse {
  groupStage: GroupStage
  bracket: KnockoutBracket
}

export interface GetGroupStageResponse {
  groupStage?: GroupStage
  bracket?: KnockoutBracket
}

export interface ReportGroupStageMatchRequest {
  seriesId: string
  groupId: string
  matchId: string
  scoreA: number
  scoreB: number
  matchTime?: string
}

export interface ReportGroupStageMatchResponse {
  groupStage: GroupStage
  match: GroupStageMatch
  bracket: KnockoutBracket
}

export interface AdvanceGroupStageResponse {
  groupStage: GroupStage
  bracket: KnockoutBracket
  qualifiedPlayerIds: string[]
}

export interface SeedKnockoutBracketRequest {
  playerIds: string[]
  strategy?: KnockoutSeedingStrategy
  hybridSeedCount?: number
}

export interface SeedKnockoutBracketResponse {
  bracket: KnockoutBracket
}

export interface PreviewKnockoutSeedingRequest {
  seriesId: string
  playerIds: string[]
  strategy?: KnockoutSeedingStrategy
  hybridSeedCount?: number
  bracketSize?: number
}

export interface PreviewKnockoutSeedingResponse {
  playerIds: string[]
}

export interface EditGroupStageMatchResponse {
  match: GroupStageMatch
  groupStage: GroupStage
  bracket: KnockoutBracket
}

export interface CompleteGroupStageResponse {
  groupStage: GroupStage
  bracket: KnockoutBracket
}

export interface EditKnockoutMatchResponse {
  match: KnockoutMatch
  bracket: KnockoutBracket
}

export interface CompleteBracketResponse {
  bracket: KnockoutBracket
}

